import { useEffect, useMemo, useRef, useState } from 'react';
import {
  groupDroneSourcesByCategory,
  droneSourceToPinnedFeed,
  type DroneSource,
} from '@tsr/shared-types';
import { usePinnedFeeds } from './pinnedFeeds';
import { showBarCaptions } from './lib/commandBarLayout';
import { useCommandBarTier } from './lib/commandBarTier';

/**
 * DRONE menu (#216 — the operator entry point for the drone feed media-tile kind).
 *
 * The drone twin of {@link NewsMenu}. A command-bar dropdown listing the curated
 * drone-feed sources, grouped by category and filterable by operator, each a
 * pin/unpin toggle. Pinning routes through the existing `kind:'drone'` pinned-feed
 * path (personal workstation state — localStorage + BroadcastChannel, NO
 * operator_actions verb, NO migration), so a pinned drone feed appears in the
 * operations-panel media region and the WALL mosaic, mirroring a camera/news pin
 * exactly.
 *
 * Drone feeds are AMBIENT + operator-gated: this is the only place they surface,
 * they never touch the map / act-register, and there is no urgency treatment here.
 * (The backing sources are a curated stub — see `drone-source.ts` — pending a real
 * drone-video ingest.) Self-contained — owns its pin state via `usePinnedFeeds`,
 * so it needs no props and drops into the command bar without threading state.
 *
 * `variant='row'` renders it as a Settings ▾ menu row instead of a bar button —
 * the demotion target when the map region is too narrow for the media menus
 * (commandBarLayout.ts overflow tier); the popover then opens to the LEFT of
 * the right-edge-hugging settings menu.
 */
export function DroneMenu({ variant = 'bar' }: { variant?: 'bar' | 'row' } = {}): JSX.Element {
  const { pins, toggle, isPinned } = usePinnedFeeds();
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);
  const captions = showBarCaptions(useCommandBarTier());

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const groups = useMemo(
    () => groupDroneSourcesByCategory(filter.trim() ? { operator: filter } : {}),
    [filter],
  );
  const pinnedDroneCount = pins.filter((p) => p.kind === 'drone').length;

  const togglePin = (source: DroneSource): void => {
    toggle(droneSourceToPinnedFeed(source));
  };

  return (
    <div
      ref={wrapRef}
      className={variant === 'row' ? 'relative' : 'relative flex shrink-0 items-center'}
      data-testid="drone-controls"
    >
      {variant === 'row' ? (
        <button
          type="button"
          data-testid="drone-menu-btn"
          onClick={() => setOpen((v) => !v)}
          title="Drone feeds — pin a live drone feed to your workstation"
          aria-haspopup="menu"
          aria-expanded={open}
          className="flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-xs text-slate-200 hover:bg-slate-800"
        >
          <span>Drone</span>
          <span className="text-slate-500" aria-hidden>
            {pinnedDroneCount > 0 ? `${pinnedDroneCount} ◂` : '◂'}
          </span>
        </button>
      ) : (
      <button
        type="button"
        data-testid="drone-menu-btn"
        onClick={() => setOpen((v) => !v)}
        title="Drone feeds — pin a live drone feed to your workstation"
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex h-7 items-center gap-1 rounded border border-slate-600 bg-slate-800 px-1.5 text-[11px] text-slate-300 hover:bg-slate-700"
      >
        {captions ? (
          <span>Drone</span>
        ) : (
        <span aria-hidden>
          {/* quadcopter glyph for the compact bar */}
          <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="6" cy="6" r="2.5" />
            <circle cx="18" cy="6" r="2.5" />
            <circle cx="6" cy="18" r="2.5" />
            <circle cx="18" cy="18" r="2.5" />
            <rect x="9.5" y="9.5" width="5" height="5" rx="1" />
            <path d="M8 8l1.5 1.5M16 8l-1.5 1.5M8 16l1.5-1.5M16 16l-1.5-1.5" strokeLinecap="round" />
          </svg>
        </span>
        )}
        <span aria-hidden>▾</span>
        {pinnedDroneCount > 0 && (
          <span className="rounded bg-slate-700 px-1 text-[10px] text-slate-300">{pinnedDroneCount}</span>
        )}
      </button>
      )}
      {open && (
        <div
          data-testid="drone-menu"
          role="menu"
          className={`absolute z-50 w-72 rounded-lg border border-slate-700 bg-slate-900/95 p-1 shadow-lg ${
            variant === 'row' ? 'right-full top-0 mr-1' : 'left-0 top-full mt-1'
          }`}
        >
          <div className="px-1 pb-1">
            <input
              data-testid="drone-filter-input"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter drone feeds…"
              className="w-full rounded border border-slate-600 bg-slate-800 px-1.5 py-1 text-xs text-slate-100 placeholder:text-slate-500"
            />
          </div>
          <p className="px-2 pb-1 text-[10px] text-slate-500">
            Ambient drone feeds — pinned to this workstation only.
          </p>
          <div className="max-h-72 space-y-1 overflow-y-auto">
            {groups.length === 0 ? (
              <p className="px-2 py-1.5 text-[11px] text-slate-500">No drone feeds match.</p>
            ) : (
              groups.map((g) => (
                <div key={g.category}>
                  <div className="px-2 pt-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                    {g.categoryLabel}
                  </div>
                  <ul className="space-y-0.5">
                    {g.sources.map((s) => {
                      const pinned = isPinned('drone', s.id);
                      return (
                        <li key={s.id}>
                          <button
                            type="button"
                            data-testid="drone-source-toggle"
                            data-pinned={pinned ? 'true' : 'false'}
                            onClick={() => togglePin(s)}
                            className="flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-xs text-slate-200 hover:bg-slate-800"
                            title={pinned ? `Unpin ${s.operator}` : `Pin ${s.operator} to this workstation`}
                          >
                            <span className="truncate">{s.operator}</span>
                            <span
                              className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium ${
                                pinned
                                  ? 'bg-violet-700/70 text-violet-100'
                                  : 'border border-slate-600 text-slate-400'
                              }`}
                            >
                              {pinned ? 'Pinned' : 'Pin'}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
